import { XCircleIcon } from '@heroicons/react/24/outline';
import React, {
  KeyboardEvent, useContext, useEffect, useState,
} from 'react';
import { StringTranslations } from '@services';
import CHDLogo from '../../images/chd-logo.svg';
import FacebookLogo from '../../images/social-icons/facebook.svg';
import LinkLogo from '../../images/social-icons/link-circle.svg';
import MailLogo from '../../images/social-icons/mail-circle.svg';
import TwitterLogo from '../../images/social-icons/twitter.svg';
import { GlobalContext } from '../GlobalContext';

type Props = {
  href: string
  lang: string
  stringTranslations: StringTranslations
}

function SocialShare({ href, lang, stringTranslations }: Props) {
  const globalContext = useContext(GlobalContext);
  const url = href;
  const [subject, setSubject] = useState("Children's Health Defense");
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
  const twitterUrl = `https://twitter.com/intent/tweet?url=${url}`;
  const emailUrl = `mailto:?subject=${subject}&body=${url}`;

  useEffect(() => {
    setSubject(window.document.title);
  }, []);

  const styles = {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    height: '30px',
    opacity: 0,
    borderTop: '2px solid #008000',
    padding: '0px 10px',
    fontSize: '16px',
    color: '#008000',
    backgroundColor: '#ccffcc',
    textAlign: 'center',
  } as React.CSSProperties;

  const [alertStyles, setAlertStyles] = useState<React.CSSProperties>(styles);

  const handleCloseModal = () => {
    if (globalContext) {
      const { showModal, setShowModal } = globalContext;
      setShowModal(!showModal);
    }
  };

  const toggleWindowPortal = (target: string,
    e: KeyboardEvent<HTMLDivElement> | undefined = undefined,
  ) => {
    if (e && e.key !== 'Enter') {
      return;
    }
    window.open(target, '', 'width=600,height=400,left=200,top=200');
  };

  const copyUrl = (text: string, e: KeyboardEvent<HTMLDivElement> | undefined = undefined) => {
    if (e && e.key !== 'Enter') {
      return;
    }
    navigator.clipboard.writeText(text);
    const visibleStyles = { ...styles };
    visibleStyles.opacity = 1;

    setAlertStyles(visibleStyles);

    setTimeout(() => {
      const fadeStyles = { ...styles };
      fadeStyles.opacity = 0;
      fadeStyles.transition = 'opacity 1s';
      setAlertStyles(fadeStyles);
    }, 2000);
  };

  const copiedMessage = (<div style={alertStyles}>{stringTranslations['link-copied'][lang]}</div>);

  return (
    <div className="w-full">
      <button type="button" className="absolute top-1 right-1" onClick={handleCloseModal}>
        <XCircleIcon className="w-[40px] text-gray-500" />
        <span className="sr-only">Close</span>
      </button>
      <div className="flex w-full">
        <img src={CHDLogo} className="w-[215px] block m-auto" alt="CHD Logo" />
      </div>
      <div className="text-xl text-black font-serif text-center mt-4 mb-4">
        {stringTranslations['share-on-social-media'][lang]}
      </div>
      <div className="flex gap-6 justify-center text-center">
        <div
          className="flex flex-col items-center"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { toggleWindowPortal(facebookUrl, e); }}
          onClick={() => { toggleWindowPortal(facebookUrl); }}
        >
          <img src={FacebookLogo} className="w-[50px]" alt="Facebook Logo" />
          <div className="text-sm">{stringTranslations.facebook[lang]}</div>
        </div>
        <div
          className="flex flex-col items-center"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { toggleWindowPortal(twitterUrl, e); }}
          onClick={() => { toggleWindowPortal(twitterUrl); }}
        >
          <img src={TwitterLogo} className="w-[50px]" alt="Twitter Logo" />
          <div className="text-sm">{stringTranslations.twitter[lang]}</div>
        </div>
        <div
          className="flex flex-col items-center"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { toggleWindowPortal(emailUrl, e); }}
          onClick={() => { toggleWindowPortal(emailUrl); }}
        >
          <img src={MailLogo} className="w-[50px]" alt="Email Logo" />
          <div className="text-sm">{stringTranslations['send-email'][lang]}</div>
        </div>
        <div
          className="flex flex-col items-center"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { copyUrl(url, e); }}
          onClick={() => { copyUrl(url); }}
        >
          <img src={LinkLogo} className="w-[50px]" alt="Copy Link Logo" />
          <div className="text-sm">{stringTranslations['copy-link'][lang]}</div>
        </div>
      </div>
      {copiedMessage}
    </div>
  );
}

export default SocialShare;
